/**
 * PaymentsHub — the admin Payments page rendered at /admin?tab=payments.
 *
 * Two subtabs (deep-linked via ?sub=tenants|management):
 *   • Tenant payments   — rent/deposit from tenants (public.payments)
 *   • Management invoices — management fees billed to owners (public.management_invoices)
 *
 * Each row has its OWN actions: Upload/View proof, Mark paid / Unmark (proof-gated),
 * and — for management — Generate/View invoice + Send. Every mutation flows through
 * the source-of-truth tables, so the dashboard revenue updates instantly.
 */
import { Fragment, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { isOffsetInvoice } from "@/types/billing";
import type { InvoiceSettlement } from "@/types/billing";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  Check,
  Undo2,
  MoreHorizontal,
  CalendarPlus,
  Eye,
  Landmark,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCents } from "@/lib/money";
import {
  useManagementInvoices,
  MANAGEMENT_INVOICE_KEYS,
  type ManagementInvoiceRow,
} from "@/hooks/v2/useManagementInvoices";
import { OwnerApartmentLink } from "@/components/admin/v2/OwnerApartmentLink";
import {
  useTenantPayments,
  useTenantPaymentMutations,
  useActiveSepaMandateUserIds,
  type TenantPaymentRow,
} from "@/hooks/v2/usePaymentsAdmin";
import { openProofInNewTab } from "@/lib/proof-url";
import {
  useManagementInvoiceActions,
  invoicePropertyLabel,
  invoicePropertyIds,
} from "./useManagementInvoiceActions";
import { RemainderInvoiceTag } from "./RemainderInvoiceTag";
import { usePaged } from "@/hooks/usePaged";
import { useLanguage } from "@/contexts/LanguageContext";

/* ------------------------------------------------------------------ helpers */

function periodLabel(dateish: string | null, language?: string): string {
  if (!dateish) return "—";
  const d = new Date(`${String(dateish).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}

function fmtDay(dateish: string | null | undefined, language?: string): string {
  if (!dateish) return "—";
  const d = new Date(`${String(dateish).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * A payment/invoice status badge.
 *
 * `settlement` is only passed for management invoices, and only matters for an
 * OFFSET one: "Due" in amber says the owner has to pay, which is false for a
 * fee already deducted from the rent CoLiviq collected. It reads as an offset
 * instead, in the same neutral blue the owner profile's pill uses, so the hub
 * and the modal do not disagree about who owes what.
 */
function StatusBadge({
  status,
  settlement,
  t,
}: {
  status: string;
  settlement?: InvoiceSettlement | null;
  t?: (key: string, fallback: string) => string;
}) {
  const translate = t ?? ((_key: string, fallback: string) => fallback);
  const map: Record<string, string> = {
    paid: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
    overdue: "bg-rose-100 text-rose-700 hover:bg-rose-100",
    pending: "bg-amber-100 text-amber-700 hover:bg-amber-100",
    scheduled: "bg-sky-100 text-sky-700 hover:bg-sky-100",
    canceled: "bg-slate-100 text-slate-500 hover:bg-slate-100",
  };
  const labels: Record<string, string> = {
    pending: translate("admin.payments.statusDue", "Due"),
    paid: translate("admin.payments.statusPaid", "Paid"),
    overdue: translate("admin.payments.statusOverdue", "Overdue"),
    scheduled: translate("admin.payments.statusScheduled", "Scheduled"),
    canceled: translate("admin.payments.statusCanceled", "Canceled"),
  };
  if (isOffsetInvoice({ settlement }) && status !== "canceled") {
    const paid = status === "paid";
    return (
      <Badge className={paid ? map.paid : map.scheduled} variant="secondary">
        {paid
          ? translate("admin.payments.statusPaidOffset", "Paid · offset")
          : translate("admin.payments.statusOffset", "Offset")}
      </Badge>
    );
  }
  const label =
    labels[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <Badge className={map[status] ?? map.pending} variant="secondary">
      {label}
    </Badge>
  );
}

const PAGE_SIZE = 5;

/** Two-letter initials for the owner avatar (e.g. "Neel Nathan" → "NN"). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Owner name in an invoice row — links into that owner's apartment (the page
 * that actually explains the invoice). One apartment goes straight there,
 * several open a picker, none falls back to the owner's profile. See
 * OwnerApartmentLink.
 */
function OwnerNameLink({
  lessorId,
  name,
  className,
  propertyIds,
}: {
  lessorId: string | null;
  name: string;
  className?: string;
  /** Properties this invoice bills — narrows which apartment we open. */
  propertyIds?: number[];
}) {
  return (
    <OwnerApartmentLink
      lessorId={lessorId}
      name={name}
      propertyIds={propertyIds}
      className={`text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none${
        className ? ` ${className}` : ""
      }`}
    />
  );
}

// Client-side pagination now lives in the shared usePaged hook (imported
// above), so the Owners list gets identical last-valid-page snap behaviour.

function PaginationBar({
  page,
  totalPages,
  total,
  noun,
  onPage,
  t,
}: {
  page: number;
  totalPages: number;
  total: number;
  /** What a paged row is ("payments", "owners") — the management tab pages by owner group, not invoice. */
  noun: string;
  onPage: (p: number) => void;
  t?: (key: string, fallback: string) => string;
}) {
  if (total <= PAGE_SIZE) return null;
  const translate = t ?? ((_key: string, fallback: string) => fallback);
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
      <span>
        {translate(
          "admin.payments.paginationRangeOf",
          "{from}–{to} of {total} {noun}"
        )
          .replace("{from}", String(from))
          .replace("{to}", String(to))
          .replace("{total}", String(total))
          .replace("{noun}", noun)}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="tabular-nums">
          {translate(
            "admin.payments.paginationPageOf",
            "Page {page} / {totalPages}"
          )
            .replace("{page}", String(page))
            .replace("{totalPages}", String(totalPages))}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** Next month as YYYY-MM-01, for the "Generate invoices" button label + payload. */
function nextMonthFirst(locale: string = "en-GB"): {
  iso: string;
  label: string;
} {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  // Month name is interpolated into a translated sentence, so it has to follow
  // the UI language — otherwise German reads "Rechnungen für March 2027 erzeugen".
  const label = d.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
  return { iso, label };
}

/* -------------------------------------------------------------- tenant tab */

// Exported for PaymentsHub.tenantActions.test.tsx: the row's action set is
// money-adjacent behaviour worth pinning on its own, and reaching it through
// the default export would drag in the management tab, the router and the
// invoice actions for no extra coverage.
export function TenantPaymentsTab() {
  const { t, language } = useLanguage();
  const { data: rows = [], isLoading } = useTenantPayments();
  const { uploadProof, markPaid, unmarkPaid, deleteProof, collectSepa } =
    useTenantPaymentMutations();
  const { data: sepaUserIds } = useActiveSepaMandateUserIds();

  const collectViaSepa = async (r: TenantPaymentRow) => {
    if (
      !window.confirm(
        t(
          "admin.payments.confirmCollectSepa",
          "Collect {amount} from {tenant} via SEPA direct debit? The tenant receives the pre-notification email and the bank settles in a few days."
        )
          .replace("{amount}", formatCents(r.amount_cents, r.currency))
          .replace(
            "{tenant}",
            r.tenantName ||
              r.tenantEmail ||
              t("admin.payments.thisTenant", "this tenant")
          )
      )
    )
      return;
    try {
      await collectSepa.mutateAsync({ id: r.id, user_id: r.user_id });
      toast.success(
        t(
          "admin.payments.toastSepaInitiated",
          "SEPA debit initiated — the payment shows as scheduled until the bank settles."
        )
      );
    } catch (err: any) {
      toast.error(
        err?.message ??
          t("admin.payments.toastSepaFailed", "SEPA collection failed")
      );
    }
  };
  const fileRef = useRef<HTMLInputElement>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  /**
   * Rent rows grouped by the PROPERTY they belong to.
   *
   * A flat list interleaved buildings across 36 pages of five, so the same
   * address turned up on page 1 and page 12 and no page answered "what does
   * Daimlerstrasse still owe?". Paging moves to the GROUP for the same reason
   * the management tab pages by owner: a page of rows would cut one building's
   * tenants in half to make room for the next building's.
   *
   * `dueCents` is deliberately the only money in the header, and it counts
   * UNSETTLED rows only — a figure that stays true when the card is collapsed
   * over its rows, unlike a total mixing paid and unpaid.
   */
  const propertyGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        title: string;
        payments: TenantPaymentRow[];
        dueCents: number;
        paid: number;
        overdue: number;
      }
    >();
    for (const r of rows as TenantPaymentRow[]) {
      const prop = r.bookings?.rooms?.properties;
      const key = prop?.id != null ? `p${prop.id}` : "unassigned";
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          title:
            prop?.title ??
            t("admin.payments.propertyUnknown", "No property linked"),
          payments: [],
          dueCents: 0,
          paid: 0,
          overdue: 0,
        };
        map.set(key, g);
      }
      g.payments.push(r);
      // The EFFECTIVE status — the one useTenantPayments derives (a pending
      // row past its due date reads as overdue) and the one the StatusBadge
      // beside it renders. Counting the raw column instead would show a row
      // badged "Paid" in this header's due figure.
      const st = r.effectiveStatus ?? r.status;
      if (st === "paid") g.paid += 1;
      else if (st === "overdue") {
        g.overdue += 1;
        g.dueCents += r.amount_cents || 0;
      } else if (st !== "canceled" && st !== "failed") {
        g.dueCents += r.amount_cents || 0;
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { numeric: true })
    );
  }, [rows, t]);

  // Buildings expand by default (empty = expanded), matching the management
  // tab's owner groups; toggling adds to `collapsed`.
  const [collapsedProps, setCollapsedProps] = useState<Set<string>>(new Set());
  const togglePropertyGroup = (key: string) =>
    setCollapsedProps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const {
    page,
    setPage,
    totalPages,
    pageRows: pageGroups,
    total,
  } = usePaged(propertyGroups, PAGE_SIZE);

  const removeProof = async (paymentId: string) => {
    if (
      !window.confirm(
        t(
          "admin.payments.confirmDeleteProof",
          "Delete this payment proof? This cannot be undone."
        )
      )
    )
      return;
    try {
      await deleteProof.mutateAsync(paymentId);
      toast.success(t("admin.payments.toastProofDeleted", "Proof deleted."));
    } catch (err: any) {
      toast.error(
        err?.message ?? t("admin.payments.toastDeleteFailed", "Delete failed")
      );
    }
  };

  const pickProof = (id: string) => {
    setTargetId(id);
    fileRef.current?.click();
  };
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !targetId) return;
    try {
      await uploadProof.mutateAsync({ paymentId: targetId, file });
      toast.success(
        t(
          "admin.payments.toastProofUploaded",
          "Proof uploaded — you can now mark this payment paid."
        )
      );
    } catch (err: any) {
      toast.error(
        err?.message ?? t("admin.payments.toastUploadFailed", "Upload failed")
      );
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={onFile}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.payments.colTenant", "Tenant")}</TableHead>
              <TableHead>
                {t("admin.payments.colPropertyRoom", "Property / Room")}
              </TableHead>
              <TableHead>{t("admin.payments.colPeriod", "Period")}</TableHead>
              <TableHead>
                {t("admin.payments.colMoveOut", "Move-out")}
              </TableHead>
              <TableHead className="text-right">
                {t("admin.payments.colAmount", "Amount")}
              </TableHead>
              <TableHead>{t("admin.payments.colStatus", "Status")}</TableHead>
              <TableHead className="text-right">
                {t("admin.payments.colAction", "Action")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-muted-foreground"
                >
                  {t(
                    "admin.payments.emptyTenantPayments",
                    "No tenant payments yet."
                  )}
                </TableCell>
              </TableRow>
            ) : (
              pageGroups.map((group) => (
                <Fragment key={group.key}>
                  <TableRow
                    className="cursor-pointer border-t bg-muted/30 hover:bg-muted/40"
                    onClick={() => togglePropertyGroup(group.key)}
                  >
                    <TableCell colSpan={7} className="py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Building2 className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {group.title}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {t(
                              "admin.payments.tenantsCount",
                              "{n} rent rows"
                            ).replace("{n}", String(group.payments.length))}
                          </div>
                        </div>
                        <span className="ml-auto flex items-center gap-3">
                          <span className="hidden items-center gap-1.5 sm:flex">
                            {group.overdue > 0 && (
                              <Badge
                                variant="outline"
                                className="border-rose-200 bg-rose-50 text-rose-700 tabular-nums dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300"
                              >
                                {t(
                                  "admin.payments.groupOverdueCount",
                                  "{n} overdue"
                                ).replace("{n}", String(group.overdue))}
                              </Badge>
                            )}
                            {/* Money, but ONLY what is unsettled — the one
                                figure that does not change meaning when the
                                rows behind it are folded away. */}
                            {group.dueCents > 0 && (
                              <Badge
                                variant="outline"
                                className="border-amber-200 bg-amber-50 text-amber-800 tabular-nums dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                              >
                                {t(
                                  "admin.payments.groupDueAmount",
                                  "{v} due"
                                ).replace(
                                  "{v}",
                                  formatCents(
                                    group.dueCents,
                                    group.payments[0]?.currency
                                  )
                                )}
                              </Badge>
                            )}
                            {group.paid > 0 && (
                              <Badge
                                variant="outline"
                                className="border-emerald-200 bg-emerald-50 text-emerald-700 tabular-nums dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
                              >
                                {t(
                                  "admin.payments.groupPaidCount",
                                  "{n} paid"
                                ).replace("{n}", String(group.paid))}
                              </Badge>
                            )}
                          </span>
                          {collapsedProps.has(group.key) ? (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                  {!collapsedProps.has(group.key) &&
                    group.payments.map((r: TenantPaymentRow) => {
                      const room = r.bookings?.rooms;
                      const prop = room?.properties;
                      const busy = uploadProof.isPending && targetId === r.id;
                      const hasProof = !!r.proofPath;
                      const status = r.effectiveStatus ?? r.status;
                      const isPaid = status === "paid";
                      // Stripe rows are owned by the gateway/webhook (COL-293/294) and
                      // terminal rows (canceled/failed) aren't payable — never offer a
                      // manual Mark paid / Unmark for either.
                      const isStripe = !!(
                        r.stripe_payment_intent_id || r.stripe_charge_id
                      );
                      const isTerminal =
                        status === "canceled" || status === "failed";
                      const canManagePaid = !isStripe && !isTerminal;
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">
                            {r.tenantName || "—"}
                            <div className="text-xs text-muted-foreground">
                              {r.tenantEmail ||
                                t(
                                  "admin.payments.noEmailOnFile",
                                  "No email on file"
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {r.type}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {prop?.title || "—"}
                            {room?.title ? ` · ${room.title}` : ""}
                          </TableCell>
                          <TableCell>
                            {periodLabel(r.due_date || r.created_at, language)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {fmtDay(r.moveOutDate, language)}
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">
                            {formatCents(r.amount_cents, r.currency)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={status} t={t} />
                          </TableCell>
                          <TableCell>
                            {/* ONE action on the surface — the step this row is
                          actually waiting for — with everything else behind
                          the kebab. Mark paid is proof-gated, so a row with no
                          proof used to render it permanently disabled with the
                          real instruction ("Upload proof first") hidden in a
                          tooltip; now the primary IS Upload proof, and Mark
                          paid appears once there is something to mark. */}
                            <div className="flex items-center justify-end gap-1.5">
                              {canManagePaid && !isPaid && !hasProof && (
                                <Button
                                  size="sm"
                                  onClick={() => pickProof(r.id)}
                                  disabled={busy}
                                  title={t(
                                    "admin.payments.actionUploadProof",
                                    "Upload proof"
                                  )}
                                >
                                  {busy ? (
                                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                  ) : (
                                    <Upload className="mr-1 h-4 w-4" />
                                  )}
                                  {t(
                                    "admin.payments.actionUploadProof",
                                    "Upload proof"
                                  )}
                                </Button>
                              )}
                              {canManagePaid && !isPaid && hasProof && (
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    markPaid.mutate({
                                      id: r.id,
                                      proofPath: r.proofPath,
                                    })
                                  }
                                  title={t(
                                    "admin.payments.actionMarkPaid",
                                    "Mark paid"
                                  )}
                                >
                                  <Check className="mr-1 h-4 w-4" />
                                  {t(
                                    "admin.payments.actionMarkPaid",
                                    "Mark paid"
                                  )}
                                </Button>
                              )}
                              {/* The menu ALWAYS renders, because it always
                                  holds at least Upload proof. Gating it on
                                  `canManagePaid` or on being unpaid would take
                                  proof upload away from the two kinds of row
                                  that most need supporting documentation and
                                  could never regain it here: a row already
                                  marked paid (an admin would have to undo the
                                  payment just to attach the receipt) and a
                                  gateway-owned or terminal row, which has no
                                  manual settle path at all. Attaching a proof
                                  settles nothing on its own — it is evidence,
                                  not an action. */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 shrink-0 p-0 text-muted-foreground data-[state=open]:bg-muted"
                                    aria-label={t(
                                      "admin.payments.paymentActionsLabel",
                                      "Payment actions"
                                    )}
                                    title={t(
                                      "admin.payments.paymentActionsLabel",
                                      "Payment actions"
                                    )}
                                  >
                                    {busy ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <MoreHorizontal className="h-4 w-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-52"
                                >
                                  {/* Skipped only when the primary button
                                        beside the kebab is already offering
                                        it — an unpaid row with no proof. */}
                                  {!hasProof && !(canManagePaid && !isPaid) && (
                                    <DropdownMenuItem
                                      onSelect={() => pickProof(r.id)}
                                      disabled={busy}
                                    >
                                      <Upload className="mr-2 h-4 w-4" />
                                      {t(
                                        "admin.payments.actionUploadProof",
                                        "Upload proof"
                                      )}
                                    </DropdownMenuItem>
                                  )}
                                  {hasProof && (
                                    <>
                                      <DropdownMenuItem
                                        onSelect={() =>
                                          void openProofInNewTab(
                                            r.proofPath
                                          ).then((ok) => {
                                            if (!ok)
                                              toast.error(
                                                t(
                                                  "admin.docs.openFailed",
                                                  "Could not open the document."
                                                )
                                              );
                                          })
                                        }
                                      >
                                        <Eye className="mr-2 h-4 w-4" />
                                        {t(
                                          "admin.payments.actionViewProof",
                                          "View proof"
                                        )}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onSelect={() => pickProof(r.id)}
                                        disabled={busy}
                                      >
                                        <Upload className="mr-2 h-4 w-4" />
                                        {t(
                                          "admin.payments.actionReplaceProof",
                                          "Replace proof"
                                        )}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onSelect={() => removeProof(r.id)}
                                        className="text-rose-600 focus:text-rose-700"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        {t(
                                          "admin.payments.actionDeleteProof",
                                          "Delete proof"
                                        )}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {canManagePaid && isPaid && (
                                    <>
                                      {hasProof && <DropdownMenuSeparator />}
                                      <DropdownMenuItem
                                        onSelect={() => unmarkPaid.mutate(r.id)}
                                      >
                                        <Undo2 className="mr-2 h-4 w-4" />
                                        {t(
                                          "admin.payments.actionUnmark",
                                          "Unmark"
                                        )}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {/* COL-89: trigger the SEPA debit for an
                                      unpaid row when the tenant has an active
                                      mandate. "scheduled" (and Stripe rows
                                      already carrying an intent) means a debit
                                      is in flight — never offer a second pull.
                                      Behind the kebab rather than in the row:
                                      it moves real money on one click, which
                                      is the last thing that should sit under a
                                      stray cursor. */}
                                  {!isPaid &&
                                    !isTerminal &&
                                    status !== "scheduled" &&
                                    !r.stripe_payment_intent_id &&
                                    !!r.user_id &&
                                    !!sepaUserIds?.has(r.user_id) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onSelect={() =>
                                            void collectViaSepa(r)
                                          }
                                          disabled={collectSepa.isPending}
                                        >
                                          {collectSepa.isPending ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          ) : (
                                            <Landmark className="mr-2 h-4 w-4" />
                                          )}
                                          {t(
                                            "admin.payments.collectSepaButton",
                                            "Collect SEPA"
                                          )}
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          noun={t("admin.payments.nounPayments", "payments")}
          onPage={setPage}
          t={t}
        />
      </Card>
    </>
  );
}

/* ---------------------------------------------------------- management tab */

function ManagementInvoicesTab() {
  const { t, language } = useLanguage();
  const { data: rows = [], isLoading } = useManagementInvoices();
  // Row actions (view invoice, proof upload/replace/delete, note, mark paid)
  // are shared with the Owners → Invoices tab.
  const { renderActions, overlays } = useManagementInvoiceActions();

  // Group invoices by owner so a multi-property owner collapses into one
  // expandable block; a single-invoice owner stays a normal flat row.
  const ownerGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        name: string;
        email: string | null;
        lessorId: string | null;
        invoices: ManagementInvoiceRow[];
      }
    >();
    for (const r of rows) {
      const key = r.lessors?.id ?? r.lessors?.email ?? `row-${r.id}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          name: r.lessors?.name || "—",
          email: r.lessors?.email ?? null,
          lessorId: r.lessors?.id ?? null,
          invoices: [],
        };
        map.set(key, g);
      }
      g.invoices.push(r);
    }
    return Array.from(map.values());
  }, [rows]);

  // Owners expand by default (empty = expanded); toggling adds to `collapsed`.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const {
    page,
    setPage,
    totalPages,
    pageRows: pageGroups,
    total,
  } = usePaged(ownerGroups, PAGE_SIZE);

  // One invoice row. `nested` = rendered under an owner group header, so the
  // owner cell is dropped and the invoice/property cells get a nesting indent.
  const renderInvoiceRow = (r: ManagementInvoiceRow, nested: boolean) => {
    const displayStatus = r.displayStatus ?? r.status;
    return (
      <TableRow key={r.id} className={nested ? "bg-muted/10" : undefined}>
        <TableCell className="font-mono text-xs font-medium">
          <span
            className={
              nested
                ? "ml-2 flex items-center border-l-2 border-primary/20 pl-3"
                : undefined
            }
          >
            {r.invoice_no}
          </span>
          <RemainderInvoiceTag
            row={r}
            className={nested ? "ml-5 block font-sans" : "block font-sans"}
          />
        </TableCell>
        <TableCell className="font-medium">
          {nested ? null : (
            <>
              <OwnerNameLink
                lessorId={r.lessors?.id ?? null}
                name={r.lessors?.name || "—"}
                propertyIds={invoicePropertyIds(r)}
              />
              <div className="text-xs text-muted-foreground font-normal">
                {r.lessors?.email ||
                  t("admin.payments.noEmailOnFile", "No email on file")}
              </div>
            </>
          )}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {(() => {
            const pl = invoicePropertyLabel(r, t);
            return nested ? (
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                <span title={pl.full ?? undefined}>
                  {pl.title}
                  {pl.city ? (
                    <span className="text-muted-foreground/70">
                      {" · "}
                      {pl.city}
                    </span>
                  ) : null}
                </span>
              </span>
            ) : (
              <span title={pl.full ?? undefined}>{pl.title}</span>
            );
          })()}
        </TableCell>
        <TableCell>{periodLabel(r.period_month, language)}</TableCell>
        <TableCell className="text-right font-semibold tabular-nums">
          {formatCents(r.amount_cents, r.currency)}
        </TableCell>
        <TableCell>
          <StatusBadge status={displayStatus} settlement={r.settlement} t={t} />
        </TableCell>
        <TableCell>{renderActions(r)}</TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.payments.colInvoice", "Invoice")}</TableHead>
              <TableHead>{t("admin.payments.colOwner", "Owner")}</TableHead>
              <TableHead>
                {t("admin.payments.colProperty", "Property")}
              </TableHead>
              <TableHead>{t("admin.payments.colPeriod", "Period")}</TableHead>
              <TableHead className="text-right">
                {t("admin.payments.colAmount", "Amount")}
              </TableHead>
              <TableHead>{t("admin.payments.colStatus", "Status")}</TableHead>
              <TableHead className="text-right">
                {t("admin.payments.colAction", "Action")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-muted-foreground"
                >
                  No management invoices yet — use “Generate invoices”.
                </TableCell>
              </TableRow>
            ) : (
              pageGroups.map((group) => {
                // Single-invoice owner → a plain flat row (no grouping chrome).
                if (group.invoices.length === 1) {
                  return renderInvoiceRow(group.invoices[0], false);
                }
                // Multi-invoice owner → an expandable owner header + nested rows.
                const isCollapsed = collapsed.has(group.key);
                // What still needs someone, NOT a summed amount. The total
                // this replaced added settled, unsettled and offset invoices
                // together, so an owner who had paid everything and one who
                // owed three months read the same — and the header is the only
                // thing left on screen once the group is collapsed over them.
                const groupCounts = group.invoices.reduce(
                  (acc, i) => {
                    const st = i.displayStatus ?? i.status;
                    if (st === "paid") acc.paid += 1;
                    else if (st === "overdue") acc.overdue += 1;
                    else if (isOffsetInvoice(i)) acc.offset += 1;
                    else acc.open += 1;
                    return acc;
                  },
                  { paid: 0, overdue: 0, offset: 0, open: 0 }
                );
                return (
                  <Fragment key={group.key}>
                    <TableRow
                      className="cursor-pointer border-t bg-muted/30 hover:bg-muted/40"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <TableCell colSpan={7} className="py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {initials(group.name)}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              <OwnerNameLink
                                lessorId={group.lessorId}
                                name={group.name}
                                propertyIds={group.invoices.flatMap(
                                  invoicePropertyIds
                                )}
                              />
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {group.email ||
                                t(
                                  "admin.payments.noEmailOnFile",
                                  "No email on file"
                                )}
                            </div>
                          </div>
                          <Badge
                            variant="secondary"
                            className="ml-1 shrink-0 gap-1"
                          >
                            <Building2 className="h-3 w-3" />
                            {group.invoices.length} invoices
                          </Badge>
                          <span className="ml-auto flex items-center gap-3">
                            <span className="hidden items-center gap-1.5 sm:flex">
                              {groupCounts.overdue > 0 && (
                                <Badge
                                  variant="outline"
                                  className="border-rose-200 bg-rose-50 text-rose-700 tabular-nums dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300"
                                >
                                  {t(
                                    "admin.payments.groupOverdueCount",
                                    "{n} overdue"
                                  ).replace("{n}", String(groupCounts.overdue))}
                                </Badge>
                              )}
                              {groupCounts.open > 0 && (
                                <Badge
                                  variant="outline"
                                  className="tabular-nums"
                                >
                                  {t(
                                    "admin.payments.groupOpenCount",
                                    "{n} open"
                                  ).replace("{n}", String(groupCounts.open))}
                                </Badge>
                              )}
                              {groupCounts.offset > 0 && (
                                <Badge
                                  variant="outline"
                                  className="border-sky-200 bg-sky-50 text-sky-700 tabular-nums dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300"
                                >
                                  {t(
                                    "admin.payments.groupOffsetCount",
                                    "{n} awaiting offset"
                                  ).replace("{n}", String(groupCounts.offset))}
                                </Badge>
                              )}
                              {groupCounts.paid > 0 && (
                                <Badge
                                  variant="outline"
                                  className="border-emerald-200 bg-emerald-50 text-emerald-700 tabular-nums dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
                                >
                                  {t(
                                    "admin.payments.groupPaidCount",
                                    "{n} paid"
                                  ).replace("{n}", String(groupCounts.paid))}
                                </Badge>
                              )}
                            </span>
                            {isCollapsed ? (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {!isCollapsed &&
                      group.invoices.map((r) => renderInvoiceRow(r, true))}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          noun={t("admin.payments.nounOwners", "owners")}
          onPage={setPage}
          t={t}
        />
      </Card>

      {overlays}
    </>
  );
}

/* ---------------------------------------------------------------- the hub */

export default function PaymentsHub() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const sub =
    searchParams.get("sub") === "management" ? "management" : "tenants";
  const { data: managementRows = [] } = useManagementInvoices();
  const overdueCount = useMemo(
    () =>
      managementRows.filter((r) => (r.displayStatus ?? r.status) === "overdue")
        .length,
    [managementRows]
  );
  const next = nextMonthFirst(language === "de" ? "de-DE" : "en-GB");

  const setSub = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "payments");
    nextParams.set("sub", value);
    setSearchParams(nextParams, { replace: true });
  };

  const generate = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("generate_management_invoices", {
        p_period: next.iso,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(
        t(
          "admin.payments.toastGenerated",
          "Generated management invoices for {month}."
        ).replace("{month}", next.label)
      );
      qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.list() });
      qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.analytics() });
    },
    onError: (e: any) =>
      toast.error(
        e?.message ??
          t("admin.payments.toastGenerationFailed", "Generation failed")
      ),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("admin.payments.pageTitle", "Payments")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "admin.payments.pageSubtitle",
              "Tenant rent and owner management invoices in one place."
            )}
          </p>
        </div>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <CalendarPlus className="h-4 w-4 mr-2" />
          )}
          {t("admin.payments.generatePrefix", "Generate")} {next.label}{" "}
          {t("admin.payments.generateSuffix", "invoices")}
        </Button>
      </div>

      <Tabs value={sub} onValueChange={setSub} className="space-y-4">
        <TabsList>
          <TabsTrigger value="tenants">
            {t("admin.payments.tabTenantPayments", "Tenant payments")}
          </TabsTrigger>
          <TabsTrigger value="management" className="gap-2">
            {t("admin.payments.tabManagementInvoices", "Management invoices")}
            {overdueCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">
                {overdueCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tenants">
          <TenantPaymentsTab />
        </TabsContent>
        <TabsContent value="management">
          <ManagementInvoicesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
